import { NeedDto } from '@act/shared/data-transfer-objects';
import { Button } from '@act/storybook';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { needRequests } from 'redux/requests/need.requests';
import { membersRequests } from 'redux/requests/members.request';
import { useAppDispatch } from 'redux/storeHooks';
import { getMemberNeeds } from 'redux/slices/needs.slice';
import { setToast } from 'redux/actions/toastActions';
import { SearchBar, SelectOption } from 'react-components/components/SearchBar';
import { useToast } from 'react-components/app/ReusableToast';

interface TransferNeedDialogProps {
  open: boolean;
  onClose: () => void;
  need: NeedDto;
  onSuccess?: () => void;
}

export const TransferNeedDialog = ({
  open,
  onClose,
  need,
  onSuccess,
}: TransferNeedDialogProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [selectedMemberOption, setSelectedMemberOption] = useState<SelectOption | null>(null);

  const [typeaheadMembers, isLoadingTypeahead, getTypeaheadMembers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );
  const [transferResult, isTransferring, transferNeed] = useApiCall(
    needRequests.transferNeedToMember
  );

  const memberOptions = useMemo(() => {
    if (!typeaheadMembers?.users) return [];
    const options = typeaheadMembers.users
      .filter(member => member.id !== need.memberId)
      .map(member => ({
        label: member.name || `${member.firstName} ${member.lastName}`,
        value: member.id,
      }));

    // If a member is selected, ensure it's in the options
    if (selectedMemberOption && !options.find(opt => opt.value === selectedMemberOption.value)) {
      options.push(selectedMemberOption);
    }

    return options;
  }, [typeaheadMembers, need.memberId, selectedMemberOption]);

  const setTypeaheadSearchTerm = (searchTerm: string) => {
    if (searchTerm) {
      getTypeaheadMembers({
        params: {
          searchTerm,
          'page-start': 0,
          'page-size': 10,
          includeCount: false,
          includeDetails: true,
        },
      });
    }
  };

  useEffect(() => {
    if (transferResult && !isTransferring) {
      toast('Need transferred successfully');
      dispatch(getMemberNeeds({ params: { memberId: need.memberId } }));
      const targetMemberId = selectedMemberOption?.value;
      if (targetMemberId) {
        dispatch(getMemberNeeds({ params: { memberId: targetMemberId } }));
      }
      onSuccess?.();
      onClose();
      setSelectedMemberOption(null);
    }
  }, [transferResult, isTransferring, toast, dispatch, need.memberId, selectedMemberOption, onSuccess, onClose]);

  const handleTransfer = () => {
    const targetMemberId = selectedMemberOption?.value;

    if (!targetMemberId) {
      dispatch(
        setToast({
          message: 'Please select a member to transfer the need to',
          severity: 'error',
        })
      );
      return;
    }

    transferNeed({
      body: {
        needId: need.id,
        targetMemberId,
      },
    });
  };

  const handleClose = () => {
    if (!isTransferring) {
      setSelectedMemberOption(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="titleLarge">Transfer Need</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="bodyMedium" sx={{ marginBottom: '16px' }}>
          Select a member to transfer this need to. All related actions and
          history will be transferred as well.
        </Typography>
        <SearchBar
          label="Select member"
          options={memberOptions}
          value={selectedMemberOption?.label || ''}
          setSearchTerm={(value: SelectOption | string) => {
            if (typeof value === 'object' && value?.value) {
              // Option selected from dropdown
              setSelectedMemberOption(value);
            } else if (typeof value === 'string' && value.trim() === '') {
              // Input cleared
              setSelectedMemberOption(null);
            }
            // Free text input - don't change the selected option (no action needed)
          }}
          setTypeaheadSearchTerm={setTypeaheadSearchTerm}
          isLoading={isLoadingTypeahead}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text" disabled={isTransferring}>
          Cancel
        </Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          disabled={!selectedMemberOption || isTransferring}
          debounced
        >
          {isTransferring ? 'Transferring...' : 'Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

